import React from 'react';
import { IconRoot, IconRootProps } from './Icon.style';

export type IconProps = IconRootProps &
  Pick<
    React.SVGProps<SVGSVGElement>,
    'fill' | 'aria-hidden' | 'viewBox' | 'stroke'
  > & {
    svgIconComponent?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  };

/**
 * Displays SVG images.
 *
 * 2 api:
 *
 * 1. Prop 'iconSrc' to pass the svg as an url that will rendered
 * as a background image.
 * 2. Prop 'svgIconComponent' to render the <svg /> element in the 'DOM'.
 *
 * Prefer the 1st method use the 2nd one only if you have to dynamically
 * change some 'svg' attribute, e.g. fill, stroke, style etc...
 *
 * @example
 * ```javascript
 * // This svg is imported as a component, see:
 * // https://create-react-app.dev/docs/adding-images-fonts-and-files/#adding-svgs
 * import Icon from '...';
 * // <PlaceholderIcon /> -> renders '<svg />'
 * import { ReactComponent as PlaceHolderIcon } from 'images/icons/placeholder-icon.svg';
 *
 * // Renders '<div className="i-set-the-size-of-the-svg"><svg /></div>'.
 * // You can override the styles of the 'div' using 'style(Icon)`[other styles]`'.
 * const WrappedComp = <Icon svgIconComponent={PlaceHolderIcon} iconSize="1.6rem" />
 * ```
 */
const Icon = ({
  fill = 'currentcolor',
  svgIconComponent: OptionalSvgIconComponent,
  stroke,
  viewBox,
  'aria-hidden': ariaHidden,
  ...rest
}: IconProps) => (
  <IconRoot aria-hidden={ariaHidden} {...rest}>
    {OptionalSvgIconComponent && (
      <OptionalSvgIconComponent
        fill={fill}
        stroke={stroke}
        {...(viewBox && { viewBox })}
      />
    )}
  </IconRoot>
);

export default Icon;
